import React from 'react';
import { motion } from 'framer-motion';
import {
  Scale, Globe, Camera, Send, ExternalLink, ArrowUp,
} from 'lucide-react';

const footerLinks = {
  Layanan: [
    'Hukum Perusahaan',
    'Sengketa Tanah & Properti',
    'Perjanjian & Kontrak',
    'Litigasi & Pengadilan',
    'Konsultasi Hukum',
    'Hukum Keluarga',
  ],
  Navigasi: [
    { label: 'Beranda',      href: '#beranda' },
    { label: 'Tentang Kami', href: '#tentang' },
    { label: 'Layanan',      href: '#layanan' },
    { label: 'Mengapa Kami', href: '#mengapa' },
    { label: 'Testimoni',    href: '#testimoni' },
    { label: 'FAQ',          href: '#faq' },
    { label: 'Kontak',       href: '#kontak' },
  ],
};

const socials = [
  { icon: Globe,   href: '#', label: 'Website' },
  { icon: Camera,  href: '#', label: 'Instagram' },
  { icon: Send,         href: '#', label: 'Telegram' },
  { icon: ExternalLink,  href: '#', label: 'LinkedIn' },
];

const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

export default function Footer() {
  return (
    <footer className="relative bg-navy-950 border-t border-white/5 overflow-hidden">
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(ellipse 50% 60% at 20% 80%, rgba(212,149,14,0.05) 0%, transparent 60%)`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main footer */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 py-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl gold-gradient flex items-center justify-center shadow-lg">
                <Scale size={22} className="text-navy-950" strokeWidth={2.5} />
              </div>
              <div>
                <p className="font-serif font-bold text-xl gold-text leading-tight">
                  Kantor Hukum
                </p>
                <p className="text-navy-400 text-xs tracking-widest uppercase">
                  Pontianak, Kalimantan Barat
                </p>
              </div>
            </div>

            <p className="text-navy-400 text-sm leading-relaxed mb-6 max-w-xs">
              Mitra hukum terpercaya Anda di Pontianak. Kami berkomitmen memberikan
              solusi hukum profesional, cepat, dan strategis untuk setiap klien.
            </p>

            {/* Social media */}
            <div className="flex gap-3">
              {socials.map(({ icon: Icon, href, label }) => (
                <motion.a
                  key={label}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.12, y: -2 }}
                  className="w-9 h-9 rounded-lg glass-light flex items-center justify-center text-navy-400 hover:text-gold-400 transition-colors duration-300 border border-white/5"
                >
                  <Icon size={16} />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Navigasi */}
          <div>
            <p className="text-white font-semibold text-sm mb-5 tracking-wide">Navigasi</p>
            <ul className="space-y-2.5">
              {footerLinks.Navigasi.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-navy-400 text-sm hover:text-gold-400 transition-colors duration-300 flex items-center gap-1.5 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-gold-600/0 group-hover:bg-gold-400 transition-all duration-300 flex-shrink-0" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Layanan */}
          <div>
            <p className="text-white font-semibold text-sm mb-5 tracking-wide">Layanan</p>
            <ul className="space-y-2.5">
              {footerLinks.Layanan.map((item) => (
                <li key={item}>
                  <span className="text-navy-400 text-sm hover:text-gold-400 transition-colors duration-300 cursor-default flex items-center gap-1.5 group">
                    <span className="w-1 h-1 rounded-full bg-gold-600/0 group-hover:bg-gold-400 transition-all duration-300 flex-shrink-0" />
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/5 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-navy-500 text-xs text-center">
            &copy; {new Date().getFullYear()} Kantor Hukum Pontianak. Seluruh hak cipta dilindungi.
          </p>
          <div className="flex items-center gap-5">
            <button type="button" className="text-navy-500 text-xs hover:text-navy-300 transition-colors">
              Kebijakan Privasi
            </button>
            <button type="button" className="text-navy-500 text-xs hover:text-navy-300 transition-colors">
              Syarat &amp; Ketentuan
            </button>
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      <motion.button
        onClick={scrollToTop}
        aria-label="Kembali ke atas"
        whileHover={{ scale: 1.1, y: -2 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-24 right-5 w-11 h-11 glass rounded-xl flex items-center justify-center text-gold-400 border border-gold-600/20 shadow-xl z-40"
        title="Kembali ke atas"
      >
        <ArrowUp size={18} />
      </motion.button>
    </footer>
  );
}
